import { createStore, createHook, Action } from 'react-sweet-state';

import { MergeQueues, MergeQueuesState } from './types';

const createSetStateAction =
  <K extends keyof MergeQueuesState>(key: K) =>
  (value: MergeQueuesState[K]): Action<MergeQueuesState> =>
  ({ setState }) => {
    setState({ [key]: value });
  };

const setFocusedMergeQueue = createSetStateAction('focusedMergeQueue');
const setFocusedPullRequest = createSetStateAction('focusedPullRequest');
const setIsCreateEditModalLoading = createSetStateAction(
  'isCreateEditModalLoading'
);
const setIsCreateModalOpen = createSetStateAction('isCreateModalOpen');
const setIsEditModalOpen = createSetStateAction('isEditModalOpen');
const setIsMergeQueuePullRequestsModalOpen = createSetStateAction(
  'isMergeQueuePullRequestsModalOpen'
);
const setIsLoading = createSetStateAction('isLoading');
const setIsEmpty = createSetStateAction('isEmpty');
const setMergeQueues = createSetStateAction('mergeQueues');
const setMergeQueuesPages = createSetStateAction('mergeQueuesPages');
const setMergeQueueActionModal = createSetStateAction('mergeQueueActionModal');

const initialState: MergeQueuesState = {
  isLoading: true,
  isCreateEditModalLoading: false,
  isCreateModalOpen: false,
  isEditModalOpen: false,
  isMergeQueuePullRequestsModalOpen: false,
  isEmpty: true,
  mergeQueueActionModal: null,
  mergeQueues: [] as MergeQueues,
  mergeQueuesPages: [],
};

export const MergeQueuesStore = createStore({
  name: 'merge-queues-store',
  initialState,
  actions: {
    setFocusedMergeQueue,
    setFocusedPullRequest,
    setIsLoading,
    setIsCreateEditModalLoading,
    setIsCreateModalOpen,
    setIsEditModalOpen,
    setIsMergeQueuePullRequestsModalOpen,
    setIsEmpty,
    setMergeQueueActionModal,
    setMergeQueues,
    setMergeQueuesPages,
  },
});

export const useMergeQueuesActions = createHook(MergeQueuesStore, {
  selector: null,
});

export const useFocusedMergeQueue = createHook(MergeQueuesStore, {
  selector: ({ focusedMergeQueue }) => ({ focusedMergeQueue }),
});

export const useFocusedPullRequest = createHook(MergeQueuesStore, {
  selector: ({ focusedPullRequest }) => ({ focusedPullRequest }),
});

export const useIsCreateEditModalLoading = createHook(MergeQueuesStore, {
  selector: ({ isCreateEditModalLoading }) => ({ isCreateEditModalLoading }),
});

export const useIsCreateModalOpen = createHook(MergeQueuesStore, {
  selector: ({ isCreateModalOpen }) => ({ isCreateModalOpen }),
});

export const useIsEditModalOpen = createHook(MergeQueuesStore, {
  selector: ({ isEditModalOpen }) => ({ isEditModalOpen }),
});

export const useIsMergeQueuePullRequestsModalOpen = createHook(
  MergeQueuesStore,
  {
    selector: ({ isMergeQueuePullRequestsModalOpen }) => ({
      isMergeQueuePullRequestsModalOpen,
    }),
  }
);

export const useMergeQueueActionModal = createHook(MergeQueuesStore, {
  selector: ({ mergeQueueActionModal }) => ({ mergeQueueActionModal }),
});

export const useIsLoading = createHook(MergeQueuesStore, {
  selector: ({ isLoading }) => ({ isLoading }),
});

export const useIsEmpty = createHook(MergeQueuesStore, {
  selector: ({ isEmpty }) => ({ isEmpty }),
});

export const useMergeQueues = createHook(MergeQueuesStore, {
  selector: ({ mergeQueues, mergeQueuesPages }) => ({
    mergeQueues,
    mergeQueuesPages,
  }),
});
